import {
  isWorkplaceRequestReservationRunResult,
  WORKPLACE_REQUEST_WORKFLOW_ACTIONS,
  type WorkplaceRequestElement,
  type WorkplaceRequestReservationRunResult,
  type WorkplaceRequestWorkflow,
  workplaceRequestContextKeyReservationPart
} from "@gettactic/api";

/**
 * The reservation question a `reservations:create` rule books from, or `null`
 * for a rule that takes its days from somewhere else — a fixed day, a plain
 * date field — or any other action.
 */
const reservationFieldOf = (
  workflow: WorkplaceRequestWorkflow
): string | null => {
  if (
    workflow.action !== WORKPLACE_REQUEST_WORKFLOW_ACTIONS.createReservation
  ) {
    return null;
  }
  const { config } = workflow;
  if (!config || !("dates" in config) || config.dates.source !== "context") {
    return null;
  }
  return (
    workplaceRequestContextKeyReservationPart(config.dates.key)?.fieldId ?? null
  );
};

/**
 * What the request's approval actually booked from reservation question
 * `fieldId`, or `null` while nothing has.
 *
 * Read from the newest successful `reservations:create` run whose rule takes
 * its days from that question: the request's own answer says what was asked
 * for, but the booking adds the rule's default attendees and the approver's
 * edits on top, and the run is the only record of that. A run is tied to its
 * rule, not to a question, so the rule's config is what says which answer it
 * booked — a type with two reservation questions has two rules, and each card
 * must show its own. Without the type's workflows nothing can be attributed.
 *
 * `workflow_runs` comes newest first and only with the single-request
 * endpoint — a request off a list has none, and reads as not booked.
 */
export const bookedReservationOf = (
  request: Pick<WorkplaceRequestElement, "workflow_runs">,
  fieldId: string,
  workflows: WorkplaceRequestWorkflow[] | undefined
): WorkplaceRequestReservationRunResult | null => {
  const workflowIds = new Set(
    (workflows ?? [])
      .filter((workflow) => reservationFieldOf(workflow) === fieldId)
      .map((workflow) => workflow.id)
  );
  if (workflowIds.size === 0) return null;

  for (const run of request.workflow_runs ?? []) {
    if (!workflowIds.has(run.workflow.id)) continue;
    if (run.status !== "success") continue;
    if (isWorkplaceRequestReservationRunResult(run.result)) return run.result;
  }
  return null;
};
